import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { IContactRequestingAssistance } from '@curbnturf/objects';
import { BASE_API_URL } from '../network/api-config';

const CONTACT_API_URL = BASE_API_URL + 'contact';

/**
 * Narrow structural slice of the server's response (ContactService.create()
 * in server/src/contact/contact.service.ts returns the created/updated
 * UserEntity as IUser) — no caller currently reads response fields, only
 * success vs. error. Kept as a named interface rather than `unknown`,
 * matching the ISuccess pattern in contact-api.service.ts.
 */
export interface IContactRequestResult {
  id?: number;
  email?: string;
}

/**
 * Local, plain-HttpClient port of @curbnturf/auth's AuthService
 * .registerRequestingHelp() — same POST {BASE_API_URL}contact endpoint and
 * IContactRequestingAssistance payload shape, without that method's
 * @curbnturf/auth NgRx/CachedHttp offline-sync wiring. Mirrors
 * contact-api.service.ts's fork rationale/pattern.
 */
@Injectable({ providedIn: 'root' })
export class ContactRequestApiService {
  private readonly http = inject(HttpClient);

  submit(request: IContactRequestingAssistance): Observable<IContactRequestResult> {
    const payload: IContactRequestingAssistance = {
      ...request,
      email: request.email.trim().toLowerCase(),
      syncId: request.syncId ?? cryptoId(),
    };
    return this.http.post<IContactRequestResult>(CONTACT_API_URL, payload);
  }
}

/** crypto.randomUUID() with a safe fallback — mirrors the cryptoId() pattern
 *  already used elsewhere in this lib (e.g. saved-searches.service.ts). */
function cryptoId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `cr-${Math.random().toString(36).slice(2, 10)}`;
}
